import { FormControl, FormErrorMessage, FormLabel, Select, useToast } from "@chakra-ui/react";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { dayjs } from "@equidefi/i18n";

import { Input } from "@equidefi/ui";
import { DateInput } from "@equidefi/ui/forms";
import { GOVERNMENT_ID_OPTIONS, US_STATE_OPTIONS } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types/dto";

// @ts-ignore
import { MESSAGE } from "@equidefi/constants/forms";
import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const dateString = (label: string) =>
  z
    .string()
    .min(1, MESSAGE.REQUIRED(label))
    .refine((value) => dayjs(value, "MM/DD/YYYY", true).isValid(), `Invalid ${label.toLowerCase()}`);

const schema = z.object({
  identification_type: z.string().min(1, MESSAGE.REQUIRED("Government ID Type")),
  issuing_authority: z.string().min(1, MESSAGE.REQUIRED("Government ID Issuing State")),
  identification_number: z.string().trim().min(1, MESSAGE.REQUIRED("Government ID Number")),
  id_issue_date: dateString("Issue Date"),
  id_expire_date: dateString("Expiration Date"),
});

type GovernmentIdFormValues = z.infer<typeof schema>;

type GovernmentIdFormNormalized = {
  identification_type: string;
  issuing_authority: string;
  identification_number: string;
  id_issue_date: string;
  id_expire_date: string;
};

type GovernmentIdFormProps = {
  profile: DTO.SavedProfile;
  onSave: (values: GovernmentIdFormNormalized) => Promise<void>;
};

const getInitialValues = (profile: DTO.SavedProfile): GovernmentIdFormValues => ({
  identification_type: profile.identification_type ?? "",
  issuing_authority: profile.issuing_authority ?? "",
  identification_number: profile.identification_number ?? "",
  id_issue_date:
    profile.id_issue_date && dayjs(profile.id_issue_date).isValid()
      ? dayjs(profile.id_issue_date).format("MM/DD/YYYY")
      : "",
  id_expire_date:
    profile.id_expire_date && dayjs(profile.id_expire_date).isValid()
      ? dayjs(profile.id_expire_date).format("MM/DD/YYYY")
      : "",
});

const normalizeValues = (values: GovernmentIdFormValues): GovernmentIdFormNormalized => ({
  identification_type: values.identification_type,
  issuing_authority: values.issuing_authority,
  identification_number: values.identification_number.trim(),
  id_issue_date: dayjs(values.id_issue_date, "MM/DD/YYYY", true).format("YYYY-MM-DD"),
  id_expire_date: dayjs(values.id_expire_date, "MM/DD/YYYY", true).format("YYYY-MM-DD"),
});

export function GovernmentIdForm({ profile, onSave }: GovernmentIdFormProps) {
  const toast = useToast();

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<GovernmentIdFormValues>({
    defaultValues: getInitialValues(profile),
    resolver: zodResolver(schema),
    mode: "onBlur",
  });

  const onSubmit = useCallback(
    async (values: GovernmentIdFormValues) => {
      try {
        await onSave(normalizeValues(values));
      } catch (error) {
        console.error(error);

        toast({
          status: "error",
          description: "There was an error trying to save your government ID.",
        });
      }
    },
    [onSave, toast],
  );

  return (
    <SavedProfileFormContainer
      profile={profile}
      title="Government ID"
      onSave={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
      isDisabled={!isValid}>
      <Controller
        name="identification_type"
        control={control}
        render={({ field, fieldState }) => (
          <FormControl isRequired isInvalid={!!fieldState.error}>
            <FormLabel>Government ID Type</FormLabel>

            <Select
              placeholder="Select ID Type"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              background="white">
              {GOVERNMENT_ID_OPTIONS.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>

            {fieldState.error && <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>}
          </FormControl>
        )}
      />

      <Controller
        name="issuing_authority"
        control={control}
        render={({ field, fieldState }) => (
          <FormControl isRequired isInvalid={!!fieldState.error}>
            <FormLabel>Government ID Issuing State</FormLabel>

            <Select
              placeholder="Select State"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              background="white">
              {US_STATE_OPTIONS.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </Select>

            {fieldState.error && <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>}
          </FormControl>
        )}
      />

      <Input
        label="Government ID Number"
        isRequired
        error={errors.identification_number?.message}
        isInvalid={!!errors.identification_number}
        {...register("identification_number")}
      />

      <DateInput
        label="Government ID Issue Date"
        placeholder="MM/DD/YYYY"
        isRequired
        error={errors.id_issue_date?.message}
        isInvalid={!!errors.id_issue_date}
        {...register("id_issue_date")}
        onChange={(date: string) => {
          setValue("id_issue_date", date);
        }}
      />

      <DateInput
        label="Government ID Expiration Date"
        placeholder="MM/DD/YYYY"
        isRequired
        error={errors.id_expire_date?.message}
        isInvalid={!!errors.id_expire_date}
        {...register("id_expire_date")}
        onChange={(date: string) => {
          setValue("id_expire_date", date);
        }}
      />
    </SavedProfileFormContainer>
  );
}
